import { useConferenciaDeCalendario } from '../../../api/queries/chamados'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { data as formatarData } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { ConferenciaDeCalendario, JornadaDoCalendario, RelogioDaFonte } from '../../../types/chamado'

/**
 * O calendário que conta os prazos — o daqui e o do e-Niterói, lado a lado.
 *
 * **Por que isto ocupa espaço numa tela de fechamento.** Todo prazo deste contrato é medido em horas
 * de expediente, e o prazo que a contratada vê no chamado foi carimbado pelo relógio do e-Niterói,
 * não por este. Quando os dois calendários discordam, o mesmo chamado pode estar no prazo de um lado
 * e fora do outro — e ninguém descobre isso na hora da contestação se a tela não disser antes.
 *
 * Nada é corrigido automaticamente: a divergência é apresentada com o efeito de cada data, e quem
 * decide é a fiscalização. Mesma regra da planilha — conflito entre fontes é achado relatado.
 *
 * @returns o cartão do calendário
 */
export function CalendarioDaApuracao() {
  const { data: conferencia, isPending, isError, error } = useConferenciaDeCalendario()
  const t = TEXTOS.calendario

  return (
    <section className="cartao space-y-4 p-5" aria-label={t.titulo}>
      <header>
        <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">{t.ajuda}</p>
      </header>

      {isPending && <Esqueleto linhas={3} />}
      {isError && <AvisoDeErro erro={error} titulo={t.indisponivel} />}
      {conferencia && <Conferencia conferencia={conferencia} />}
    </section>
  )
}

function Conferencia({ conferencia }: { conferencia: ConferenciaDeCalendario }) {
  const t = TEXTOS.calendario

  return (
    <div className="space-y-4">
      <dl className="grid gap-3 sm:grid-cols-2">
        <Jornada rotulo={t.jornadaDoContrato} jornada={conferencia.jornadaDoContrato} />
        {conferencia.jornadaDoENiteroi && (
          <Jornada rotulo={t.jornadaDoENiteroi} jornada={conferencia.jornadaDoENiteroi} />
        )}
      </dl>

      {conferencia.disponivel && (
        <p
          className={
            conferencia.jornadaDivergente
              ? 'max-w-prose rounded-lg border border-laranja-200 bg-laranja-50 px-3 py-2 text-sm text-slate-800'
              : 'text-sm text-slate-600'
          }
        >
          {conferencia.jornadaDivergente ? t.jornadaDivergente : t.jornadaIgual}
        </p>
      )}

      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium text-slate-500">{t.diasNoContrato}</dt>
          <dd className="text-sm text-slate-800">
            {conferencia.diasNoContrato}
            {conferencia.diasNaoConferidos > 0 && (
              <span className="text-slate-500"> · {conferencia.diasNaoConferidos} {t.naoConferidos}</span>
            )}
          </dd>
        </div>
        {conferencia.disponivel && (
          <div>
            <dt className="text-xs font-medium text-slate-500">{t.diasNoENiteroi}</dt>
            <dd className="text-sm text-slate-800">{conferencia.diasNoENiteroi}</dd>
          </div>
        )}
      </dl>

      {!conferencia.disponivel && conferencia.motivoIndisponivel && (
        <p className="max-w-prose rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <span className="font-medium">{t.indisponivel}.</span> {conferencia.motivoIndisponivel}
        </p>
      )}

      {conferencia.relogio && <Relogio relogio={conferencia.relogio} />}

      {conferencia.disponivel && <Divergencias conferencia={conferencia} />}
    </div>
  )
}

/**
 * O relógio em que as datas do e-Niterói são lidas.
 *
 * Aparece mesmo quando está tudo certo, e isso é deliberado: erro de fuso é o defeito que não tem
 * sintoma — as datas continuam bem formadas, os prazos continuam sendo calculados, e o único efeito
 * visível é um chamado cair do lado errado da borda do expediente. Quem assina um termo de
 * recebimento precisa poder conferir que o relógio confere.
 *
 * @param props a conferência do relógio
 * @returns a linha do relógio
 */
function Relogio({ relogio }: { relogio: RelogioDaFonte }) {
  const t = TEXTOS.calendario
  const certo = relogio.divergenciaEmHoras === 0
  return (
    <p
      className={
        certo
          ? 'text-sm text-slate-600'
          : 'max-w-prose rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-slate-800'
      }
    >
      <span className="font-medium">{t.relogio}:</span> {relogio.fusoAssumido}.{' '}
      {certo ? t.relogioCerto : relogio.explicacao}
    </p>
  )
}

function Divergencias({ conferencia }: { conferencia: ConferenciaDeCalendario }) {
  const t = TEXTOS.calendario
  if (conferencia.divergencias.length === 0) {
    return <p className="text-sm text-slate-600">{t.semDivergencia}</p>
  }
  return (
    <div>
      <h3 className="text-sm font-medium text-slate-700">
        {t.divergencias} ({conferencia.divergencias.length})
      </h3>
      <p className="mt-1 max-w-prose text-xs text-slate-500">{t.comoUsar}</p>
      <ul className="mt-2 space-y-2">
        {conferencia.divergencias.map((divergencia) => (
          <li key={divergencia.data} className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold tabular-nums text-slate-800">
                {formatarData(divergencia.data)}
              </span>
              <span
                className={
                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' +
                  (divergencia.tipo === 'SO_NO_CONTRATO'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-200 text-slate-700')
                }
              >
                {divergencia.tipo === 'SO_NO_CONTRATO' ? t.soNoContrato : t.soNoENiteroi}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600">{divergencia.efeito}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Jornada({ rotulo, jornada }: { rotulo: string; jornada: JornadaDoCalendario }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{rotulo}</dt>
      <dd className="text-sm text-slate-800">
        {jornada.inicio && jornada.fim ? `${jornada.inicio} às ${jornada.fim}` : '—'}
        <span className="block text-xs text-slate-500">{jornada.fonte}</span>
      </dd>
    </div>
  )
}
